// components/BodyMap.tsx
// Interactive anatomical SVG human body for physiotherapy diagrams.
// Anatomical body shapes drawn with Bezier paths + radial 3D gradient fills.
// Transparent hit-zones overlay the silhouette; same Firebase region IDs kept stable.
import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { DiagramAnnotation } from './DiagramViewer';

interface BodyMapProps {
  annotations: Record<string, DiagramAnnotation>;
  onRegionClick: (regionId: string) => void;
}

const STATUS_COLORS: Record<string, { fill: string; stroke: string }> = {
  normal:  { fill: 'transparent', stroke: 'transparent' },
  problem: { fill: '#ff4d4f',     stroke: '#ff4d4f'     },
  treated: { fill: '#52c41a',     stroke: '#52c41a'     },
  missing: { fill: '#8c8c8c',     stroke: '#8c8c8c'     },
};

type RegionShape =
  | { type: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { type: 'rect';    x: number;  y: number;  w: number;  h: number; rx?: number };

interface Region {
  id: string;
  label: string;
  shape: RegionShape;
  view: 'front' | 'back' | 'both';
}

const shapeCenter = (s: RegionShape) =>
  s.type === 'ellipse'
    ? { x: s.cx, y: s.cy }
    : { x: s.x + s.w / 2, y: s.y + s.h / 2 };

const notePos = (s: RegionShape) =>
  s.type === 'ellipse'
    ? { x: s.cx + s.rx - 5, y: s.cy - s.ry + 5 }
    : { x: s.x + s.w - 5,  y: s.y + 5 };

// Region IDs are stored in Firebase — never rename them.
const REGIONS: Region[] = [
  // ── Head & Neck ─────────────────────────────────────────────────
  { id: 'head',           label: 'Head',         shape: { type: 'ellipse', cx: 110, cy: 40,  rx: 27, ry: 31 }, view: 'both'  },
  { id: 'neck',           label: 'Neck',          shape: { type: 'rect',    x: 97,  y: 68,  w: 26, h: 26, rx: 8  }, view: 'both'  },
  // ── Shoulders ───────────────────────────────────────────────────
  { id: 'left_shoulder',  label: 'L. Shoulder',   shape: { type: 'ellipse', cx: 56,  cy: 100, rx: 28, ry: 22 }, view: 'both'  },
  { id: 'right_shoulder', label: 'R. Shoulder',   shape: { type: 'ellipse', cx: 164, cy: 100, rx: 28, ry: 22 }, view: 'both'  },
  // ── Torso front ─────────────────────────────────────────────────
  { id: 'chest',          label: 'Chest',          shape: { type: 'rect',    x: 68,  y: 92,  w: 84, h: 64, rx: 12 }, view: 'front' },
  { id: 'abdomen',        label: 'Abdomen',        shape: { type: 'rect',    x: 70,  y: 154, w: 80, h: 48, rx: 10 }, view: 'front' },
  // ── Torso back ──────────────────────────────────────────────────
  { id: 'upper_back',     label: 'Upper Back',    shape: { type: 'rect',    x: 68,  y: 92,  w: 84, h: 64, rx: 12 }, view: 'back'  },
  { id: 'lower_back',     label: 'Lower Back',    shape: { type: 'rect',    x: 70,  y: 154, w: 80, h: 48, rx: 10 }, view: 'back'  },
  // ── Arms ────────────────────────────────────────────────────────
  { id: 'left_arm',       label: 'L. Upper Arm',  shape: { type: 'rect',    x: 12,  y: 104, w: 34, h: 84, rx: 15 }, view: 'both'  },
  { id: 'right_arm',      label: 'R. Upper Arm',  shape: { type: 'rect',    x: 174, y: 104, w: 34, h: 84, rx: 15 }, view: 'both'  },
  { id: 'left_forearm',   label: 'L. Forearm',    shape: { type: 'rect',    x: 5,   y: 196, w: 28, h: 78, rx: 12 }, view: 'both'  },
  { id: 'right_forearm',  label: 'R. Forearm',    shape: { type: 'rect',    x: 187, y: 196, w: 28, h: 78, rx: 12 }, view: 'both'  },
  { id: 'left_hand',      label: 'L. Hand',        shape: { type: 'ellipse', cx: 16,  cy: 299, rx: 14, ry: 22 }, view: 'both'  },
  { id: 'right_hand',     label: 'R. Hand',        shape: { type: 'ellipse', cx: 204, cy: 299, rx: 14, ry: 22 }, view: 'both'  },
  // ── Hips / Pelvis ───────────────────────────────────────────────
  { id: 'left_hip',       label: 'L. Hip',         shape: { type: 'rect',    x: 57,  y: 200, w: 44, h: 38, rx: 14 }, view: 'both'  },
  { id: 'right_hip',      label: 'R. Hip',         shape: { type: 'rect',    x: 119, y: 200, w: 44, h: 38, rx: 14 }, view: 'both'  },
  // ── Legs ────────────────────────────────────────────────────────
  { id: 'left_thigh',     label: 'L. Thigh',       shape: { type: 'rect',    x: 57,  y: 236, w: 42, h: 90, rx: 16 }, view: 'both'  },
  { id: 'right_thigh',    label: 'R. Thigh',       shape: { type: 'rect',    x: 121, y: 236, w: 42, h: 90, rx: 16 }, view: 'both'  },
  { id: 'left_knee',      label: 'L. Knee',        shape: { type: 'ellipse', cx: 78,  cy: 344, rx: 22, ry: 18 }, view: 'both'  },
  { id: 'right_knee',     label: 'R. Knee',        shape: { type: 'ellipse', cx: 142, cy: 344, rx: 22, ry: 18 }, view: 'both'  },
  { id: 'left_calf',      label: 'L. Calf',        shape: { type: 'rect',    x: 58,  y: 360, w: 40, h: 84, rx: 16 }, view: 'both'  },
  { id: 'right_calf',     label: 'R. Calf',        shape: { type: 'rect',    x: 122, y: 360, w: 40, h: 84, rx: 16 }, view: 'both'  },
  { id: 'left_foot',      label: 'L. Foot',        shape: { type: 'ellipse', cx: 77,  cy: 464, rx: 26, ry: 18 }, view: 'both'  },
  { id: 'right_foot',     label: 'R. Foot',        shape: { type: 'ellipse', cx: 143, cy: 464, rx: 26, ry: 18 }, view: 'both'  },
];

const BODY_W = 220;
const BODY_H = 492;

const BodyMap: React.FC<BodyMapProps> = ({ annotations, onRegionClick }) => {
  const [view, setView] = useState<'front' | 'back'>('front');
  const [hovered, setHovered] = useState<string | null>(null);

  const visible = REGIONS.filter(r => r.view === 'both' || r.view === view);

  // ── Render a transparent hit-shape (click zone) ─────────────────
  const hitShape = (
    s: RegionShape,
    fill: string,
    fillOpacity: number,
    stroke: string,
    strokeWidth: number,
  ): JSX.Element => {
    const shared = { fill, fillOpacity, stroke, strokeWidth } as const;
    if (s.type === 'ellipse')
      return <ellipse cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} {...shared} />;
    return <rect x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx ?? 8} {...shared} />;
  };

  const renderRegion = (r: Region) => {
    const ann        = annotations[r.id];
    const statusKey  = ann?.status ?? 'normal';
    const colors     = STATUS_COLORS[statusKey] ?? STATUS_COLORS.normal;
    const isAnnotated = !!ann && statusKey !== 'normal';
    const isHovered  = hovered === r.id;
    const center     = shapeCenter(r.shape);
    const np         = notePos(r.shape);
    const tip = ann
      ? `${r.label}: ${statusKey}${ann.note ? ' — ' + ann.note : ''}`
      : `${r.label} — click to annotate`;

    return (
      <Tooltip key={r.id} title={tip} mouseEnterDelay={0.15}>
        <g
          style={{ cursor: 'pointer' }}
          onClick={() => onRegionClick(r.id)}
          onMouseEnter={() => setHovered(r.id)}
          onMouseLeave={() => setHovered(null)}
        >
          {/* Annotation colour fill */}
          {hitShape(
            r.shape,
            isAnnotated ? colors.fill : (isHovered ? '#6366f1' : 'transparent'),
            isAnnotated ? 0.28 : (isHovered ? 0.1 : 0),
            isAnnotated ? colors.stroke : (isHovered ? '#6366f1' : 'transparent'),
            isAnnotated ? 2 : (isHovered ? 1.8 : 0),
          )}

          {/* Label on annotated regions */}
          {isAnnotated && (
            <text
              x={center.x} y={center.y + 4}
              textAnchor="middle" fontSize={7} fontWeight="700"
              fill="white" stroke={colors.stroke}
              strokeWidth={2.5} paintOrder="stroke"
              style={{ pointerEvents: 'none' }}
            >
              {r.label}
            </text>
          )}

          {/* Note dot (blue) */}
          {ann?.note && (
            <circle cx={np.x} cy={np.y} r={3.5} fill="#1677ff"
              style={{ pointerEvents: 'none' }} />
          )}

          {/* Hover label (unannotated) */}
          {isHovered && !isAnnotated && (
            <text
              x={center.x} y={center.y + 4}
              textAnchor="middle" fontSize={7.5} fontWeight="600"
              fill="#6366f1"
              style={{ pointerEvents: 'none' }}
            >
              {r.label}
            </text>
          )}
        </g>
      </Tooltip>
    );
  };

  // ── Body silhouette path data (220 × 492 viewBox) ───────────────
  // All shapes use fill="url(#bm-bodyGrad)" which gives 3-D depth.
  // Right-side paths are exact mirror images (x → 220 − x).
  const silhouette = (
    <g style={{ pointerEvents: 'none' }}>

      {/* Torso — chest narrows to waist, expands at hips */}
      <path
        d="M 70,88 C 64,98 62,115 62,132 C 61,150 62,165 65,178
           C 67,188 70,196 75,200 L 145,200
           C 150,196 153,188 155,178 C 158,165 159,150 158,132
           C 158,115 156,98 150,88 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5"
      />

      {/* Pelvis — wider than waist */}
      <path
        d="M 72,200 C 60,210 54,220 54,236 L 166,236
           C 166,220 160,210 148,200 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5"
      />

      {/* Shoulders */}
      <ellipse cx={56}  cy={100} rx={30} ry={22}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
      <ellipse cx={164} cy={100} rx={30} ry={22}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />

      {/* Upper arms — tapered cylinders */}
      <path d="M 26,102 C 14,124 10,154 12,192 L 34,192 C 36,154 42,124 50,104 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />
      <path d="M 194,102 C 206,124 210,154 208,192 L 186,192 C 184,154 178,124 170,104 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />

      {/* Forearms — slightly angled */}
      <path d="M 10,192 C 6,216 4,246 5,278 L 25,278 C 28,246 30,216 32,192 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />
      <path d="M 210,192 C 214,216 216,246 215,278 L 195,278 C 192,246 190,216 188,192 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />

      {/* Hands */}
      <path d="M 3,276 C 1,290 1,304 5,316 C 9,324 16,326 16,326
               C 23,324 27,316 31,304 C 31,290 29,276 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />
      <path d="M 217,276 C 219,290 219,304 215,316 C 211,324 204,326 204,326
               C 197,324 193,316 189,304 C 189,290 191,276 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />

      {/* Thighs */}
      <path d="M 56,234 C 54,265 54,298 56,330 L 100,330 C 102,298 102,265 100,234 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />
      <path d="M 164,234 C 166,265 166,298 164,330 L 120,330 C 118,298 118,265 120,234 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />

      {/* Knees — prominent rounded caps */}
      <ellipse cx={78}  cy={344} rx={22} ry={18}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />
      <ellipse cx={142} cy={344} rx={22} ry={18}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />

      {/* Calves — tapered */}
      <path d="M 58,358 C 56,386 58,416 62,446 L 96,446 C 100,416 102,386 100,358 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />
      <path d="M 162,358 C 164,386 162,416 158,446 L 124,446 C 120,416 118,386 120,358 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.45" />

      {/* Ankles — visual bridge */}
      <ellipse cx={79}  cy={450} rx={18} ry={10}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />
      <ellipse cx={141} cy={450} rx={18} ry={10}
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />

      {/* Feet — naturally splayed outward */}
      <path d="M 60,453 C 52,455 48,462 50,470 C 52,478 66,484 80,482
               C 94,480 100,474 100,466 C 100,460 98,455 92,452 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />
      <path d="M 160,453 C 168,455 172,462 170,470 C 168,478 154,484 140,482
               C 126,480 120,474 120,466 C 120,460 122,455 128,452 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.4" />

      {/* Neck */}
      <path d="M 100,68 Q 110,66 120,68 L 118,92 Q 110,93 102,92 Z"
        fill="url(#bm-bodyGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />

      {/* Head — drawn last so it sits above neck */}
      <ellipse cx={110} cy={40} rx={27} ry={31}
        fill="url(#bm-headGrad)" stroke="#64748b" strokeWidth="0.8" strokeOpacity="0.5" />

      {/* ── Anatomical detail lines ──────────────────────────────── */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {view === 'front' ? (
          <>
            {/* Collar bones */}
            <path d="M 104,88 C 90,84 74,90 58,100"  stroke="#475569" strokeOpacity="0.2"  strokeWidth="1.3" />
            <path d="M 116,88 C 130,84 146,90 162,100" stroke="#475569" strokeOpacity="0.2"  strokeWidth="1.3" />
            {/* Sternum */}
            <line x1={110} y1={90}  x2={110} y2={130} stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            {/* Pectoral folds */}
            <path d="M 94,118 C 100,126 107,132 101,146" stroke="#475569" strokeOpacity="0.11" strokeWidth="1.0" />
            <path d="M 126,118 C 120,126 113,132 119,146" stroke="#475569" strokeOpacity="0.11" strokeWidth="1.0" />
            {/* Abdominal centre line */}
            <line x1={110} y1={130} x2={110} y2={200} stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            {/* Abdominal segments (3) */}
            <path d="M 93,148 Q 110,151 127,148" stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            <path d="M 93,163 Q 110,166 127,163" stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            <path d="M 93,178 Q 110,181 127,178" stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            {/* Navel */}
            <circle cx={110} cy={174} r={2.5} stroke="#475569" strokeOpacity="0.18" strokeWidth="1.0" />
            {/* Groin / inguinal lines */}
            <path d="M 82,200 C 77,210 70,220 62,232"  stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            <path d="M 138,200 C 143,210 150,220 158,232" stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            {/* Knee patellae */}
            <circle cx={78}  cy={344} r={9} stroke="#475569" strokeOpacity="0.18" strokeWidth="1.0" />
            <circle cx={142} cy={344} r={9} stroke="#475569" strokeOpacity="0.18" strokeWidth="1.0" />
            {/* Elbow creases */}
            <path d="M 12,192 Q 23,197 34,192"   stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            <path d="M 188,192 Q 197,197 208,192" stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            {/* Wrist creases */}
            <line x1={5}   y1={278} x2={25}  y2={278} stroke="#475569" strokeOpacity="0.12" strokeWidth="1.0" />
            <line x1={195} y1={278} x2={215} y2={278} stroke="#475569" strokeOpacity="0.12" strokeWidth="1.0" />
          </>
        ) : (
          <>
            {/* Spine — dashed centreline */}
            <line x1={110} y1={90} x2={110} y2={236}
              stroke="#475569" strokeOpacity="0.22" strokeWidth="1.2" strokeDasharray="4,3" />
            {/* Trapezius V */}
            <path d="M 68,92 C 80,98 100,104 110,108 C 120,104 140,98 152,92"
              stroke="#475569" strokeOpacity="0.13" strokeWidth="1.2" />
            {/* Left scapula */}
            <path d="M 88,102 C 76,114 72,132 78,150 C 86,162 100,160 104,142"
              stroke="#475569" strokeOpacity="0.2" strokeWidth="1.2" />
            <line x1={90} y1={108} x2={92} y2={148} stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            {/* Right scapula */}
            <path d="M 132,102 C 144,114 148,132 142,150 C 134,162 120,160 116,142"
              stroke="#475569" strokeOpacity="0.2" strokeWidth="1.2" />
            <line x1={130} y1={108} x2={128} y2={148} stroke="#475569" strokeOpacity="0.13" strokeWidth="1.0" />
            {/* Lumbar dimples */}
            <circle cx={100} cy={226} r={3.5} stroke="#475569" strokeOpacity="0.28" strokeWidth="1.0" />
            <circle cx={120} cy={226} r={3.5} stroke="#475569" strokeOpacity="0.28" strokeWidth="1.0" />
            {/* Gluteal crease */}
            <line x1={110} y1={236} x2={110} y2={262} stroke="#475569" strokeOpacity="0.18" strokeWidth="1.2" />
            {/* Achilles tendons */}
            <line x1={79}  y1={446} x2={79}  y2={460} stroke="#475569" strokeOpacity="0.22" strokeWidth="1.5" />
            <line x1={141} y1={446} x2={141} y2={460} stroke="#475569" strokeOpacity="0.22" strokeWidth="1.5" />
            {/* Calf muscle lines */}
            <line x1={78}  y1={364} x2={78}  y2={408} stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            <line x1={142} y1={364} x2={142} y2={408} stroke="#475569" strokeOpacity="0.10" strokeWidth="1.0" />
            {/* Elbow creases (back) */}
            <path d="M 12,192 Q 23,196 34,192"   stroke="#475569" strokeOpacity="0.12" strokeWidth="1.0" />
            <path d="M 188,192 Q 197,196 208,192" stroke="#475569" strokeOpacity="0.12" strokeWidth="1.0" />
          </>
        )}
      </g>
    </g>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>

      {/* View toggle */}
      <div style={{ display: 'inline-flex', background: '#f0f2f5', borderRadius: 8, padding: 3, gap: 3 }}>
        {(['front', 'back'] as const).map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: '5px 18px', borderRadius: 6, border: 'none',
              cursor: 'pointer', fontSize: 13,
              fontWeight: view === v ? 600 : 400,
              background: view === v ? '#fff' : 'transparent',
              color: view === v ? '#1677ff' : '#667085',
              boxShadow: view === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)} View
          </button>
        ))}
      </div>

      {/* SVG body */}
      <svg
        width={BODY_W}
        height={BODY_H}
        viewBox={`0 0 ${BODY_W} ${BODY_H}`}
        style={{
          maxWidth: '100%',
          borderRadius: 12,
          background: 'linear-gradient(180deg, #f0f4f8 0%, #e8edf4 100%)',
          border: '1px solid #e4e7ec',
        }}
      >
        <defs>
          {/* 3-D body gradient — radial, highlight from upper-left */}
          <radialGradient id="bm-bodyGrad" gradientUnits="objectBoundingBox"
            cx="0.3" cy="0.25" r="0.78">
            <stop offset="0%"   stopColor="#dce8f4" />
            <stop offset="50%"  stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#455a6e" />
          </radialGradient>

          {/* Slightly warmer gradient for the head */}
          <radialGradient id="bm-headGrad" gradientUnits="objectBoundingBox"
            cx="0.4" cy="0.3" r="0.72">
            <stop offset="0%"   stopColor="#e4edf8" />
            <stop offset="55%"  stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#3d5066" />
          </radialGradient>

          {/* Drop shadow for the whole body group */}
          <filter id="bm-shadow" x="-25%" y="-10%" width="150%" height="135%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.5"
              floodColor="#1a2a38" floodOpacity="0.18" />
          </filter>
        </defs>

        {/* Anatomical silhouette (non-interactive) */}
        <g filter="url(#bm-shadow)">
          {silhouette}
        </g>

        {/* Clickable hit-zones (rendered on top) */}
        {visible.map(renderRegion)}
      </svg>

      {/* Legend */}
      <div style={{
        display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap',
        padding: '8px 12px', background: '#f8fafc', borderRadius: 8,
        border: '1px solid #eaecf0', width: '100%', maxWidth: 340,
      }}>
        {[
          { key: 'problem', label: 'Problem' },
          { key: 'treated', label: 'Treated' },
          { key: 'missing', label: 'Missing'  },
        ].map(({ key, label }) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475467' }}>
            <span style={{
              width: 12, height: 12, borderRadius: 3,
              background: STATUS_COLORS[key].fill,
              border: `2px solid ${STATUS_COLORS[key].stroke}`,
              display: 'inline-block', flexShrink: 0,
            }} />
            {label}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#98a2b3' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#1677ff', display: 'inline-block' }} />
          Note
        </span>
      </div>
    </div>
  );
};

export default BodyMap;
